// Shared Bible chapter fetch + cache — pulled out of BibleApp.jsx's reader
// (docs/FIX-PLANS.md §3a: "Fetch it using the reader's existing chapter
// fetch and the user's translation... move that into a shared helper rather
// than duplicating it"). Two consumers: BibleApp.jsx's own reader (which
// keeps its own bibleLoad/bibleErr/soft-fail-to-KJV UI state around this)
// and ReadingPlanDaySheet.jsx's Bible reading step.
//
// KJV / WEB / ASV / YLT → bible-api.com (free, no key, CORS)
// ESV                   → Crossway via claude-proxy `fetch_esv` (server-side key)
// NIV                   → API.Bible via claude-proxy `fetch_niv` (server-side key)

import { supabase } from "../supabase";

// Menu order: ESV first (also the default for new users), then NIV, then
// the free public-domain texts.
export const VERSIONS = ["ESV", "NIV", "KJV", "WEB"];

export const TRANSLATION_ATTRIBUTION = {
  ESV: "ESV® Bible, © Crossway",
  NIV: "NIV® © Biblica",
};

// Map book display names to bible-api.com URL format (lowercase, spaces to +)
export function bookToApiName(bookName) {
  return bookName.toLowerCase()
    .replace(/\s+/g, '+')
    .replace(/1\+/g, '1+')
    .replace(/2\+/g, '2+')
    .replace(/3\+/g, '3+');
}

// ── localStorage chapter cache (translation-aware) ────────────────────
// Speeds up navigation + keeps ESV/NIV calls under their free quotas.
// Key shape: `bible_{translation}_{book}_{chapter}` → JSON array of {v,t}.
function bibleCacheKey(translation, book, chapter) {
  return `bible_${translation}_${book}_${chapter}`;
}
function loadCachedChapter(translation, book, chapter) {
  try {
    const raw = localStorage.getItem(bibleCacheKey(translation, book, chapter));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch { return null; }
}
function saveCachedChapter(translation, book, chapter, verses) {
  try {
    localStorage.setItem(bibleCacheKey(translation, book, chapter), JSON.stringify(verses));
  } catch (e) {
    // QuotaExceeded — silently ignore. In-memory chapterCache still works.
    // eslint-disable-next-line no-console
    console.warn("[bible cache] could not persist chapter:", e.message);
  }
}

// ── ESV / NIV fetchers (via claude-proxy edge function) ────────────────
async function fetchESVChapter(book, chapter) {
  const { data, error } = await supabase.functions.invoke("claude-proxy", {
    body: { action: "fetch_esv", book, chapter },
  });
  if (error) {
    const upstream = data?.error || data?.detail || error.message || "ESV fetch failed";
    throw new Error(upstream);
  }
  const verses = Array.isArray(data?.verses) ? data.verses : [];
  if (verses.length === 0) throw new Error("ESV returned no verses");
  return verses;
}

async function fetchNIVChapter(book, chapter) {
  const { data, error } = await supabase.functions.invoke("claude-proxy", {
    body: { action: "fetch_niv", book, chapter },
  });
  if (error) {
    const upstream = data?.error || data?.detail || error.message || "NIV fetch failed";
    throw new Error(upstream);
  }
  const verses = Array.isArray(data?.verses) ? data.verses : [];
  if (verses.length === 0) throw new Error("NIV returned no verses");
  return verses;
}

// Verified on app mount, then cached in localStorage so the answer survives
// reloads without another round-trip. Returns true if the configured
// BIBLE_API_KEY on the edge function has NIV access.
export async function checkNivAvailability() {
  try {
    const { data, error } = await supabase.functions.invoke("claude-proxy", {
      body: { action: "check_niv_available" },
    });
    if (error) return false;
    return !!data?.available;
  } catch {
    return false;
  }
}

// ── Seed data: Romans 8 KJV - displayed immediately, no API call needed
export const SEED_VERSES = [
  {v:1,t:"There is therefore now no condemnation to them which are in Christ Jesus, who walk not after the flesh, but after the Spirit."},
  {v:2,t:"For the law of the Spirit of life in Christ Jesus hath made me free from the law of sin and death."},
  {v:3,t:"For what the law could not do, in that it was weak through the flesh, God sending his own Son in the likeness of sinful flesh, and for sin, condemned sin in the flesh:"},
  {v:4,t:"That the righteousness of the law might be fulfilled in us, who walk not after the flesh, but after the Spirit."},
  {v:5,t:"For they that are after the flesh do mind the things of the flesh; but they that are after the Spirit the things of the Spirit."},
  {v:6,t:"For to be carnally minded is death; but to be spiritually minded is life and peace."},
  {v:7,t:"Because the carnal mind is enmity against God: for it is not subject to the law of God, neither indeed can be."},
  {v:8,t:"So then they that are in the flesh cannot please God."},
  {v:9,t:"But ye are not in the flesh, but in the Spirit, if so be that the Spirit of God dwell in you. Now if any man have not the Spirit of Christ, he is none of his."},
  {v:10,t:"And if Christ be in you, the body is dead because of sin; but the Spirit is life because of righteousness."},
  {v:11,t:"But if the Spirit of him that raised up Jesus from the dead dwell in you, he that raised up Christ from the dead shall also quicken your mortal bodies by his Spirit that dwelleth in you."},
  {v:12,t:"Therefore, brethren, we are debtors, not to the flesh, to live after the flesh."},
];

// In-memory cache for this session — module-level so it survives across
// component mounts/unmounts (e.g. switching tabs away from and back to the
// reader, or opening the plan day sheet after the main reader already
// fetched the same chapter). Exported (a live, mutable reference) so
// BibleApp.jsx's synchronous cache peeks (verseText(), ensureChapterLoaded's
// already-loaded guard) keep working without going through fetchBibleChapter.
export const chapterCache = { "KJV/Romans/8": SEED_VERSES };

// Fetch one chapter, dispatching to the right backend by translation,
// checking the in-memory cache then the localStorage cache first, and
// writing both on a fresh fetch. Throws on failure — callers decide the UI
// treatment (BibleApp.jsx's reader soft-fails to KJV; ReadingPlanDaySheet
// just shows an error).
export async function fetchBibleChapter(ver, book, chap) {
  const cacheKey = `${ver}/${book}/${chap}`;

  if (cacheKey === "KJV/Romans/8") {
    chapterCache[cacheKey] = SEED_VERSES;
    return SEED_VERSES;
  }
  if (chapterCache[cacheKey]) return chapterCache[cacheKey];
  const persisted = loadCachedChapter(ver, book, chap);
  if (persisted) {
    chapterCache[cacheKey] = persisted;
    return persisted;
  }

  const bibleApiTranslations = new Set(["KJV", "WEB", "ASV", "YLT"]);
  const loader = ver === "ESV" ? () => fetchESVChapter(book, chap)
    : ver === "NIV" ? () => fetchNIVChapter(book, chap)
    : bibleApiTranslations.has(ver)
      ? () => fetch(`https://bible-api.com/${bookToApiName(book)}+${chap}?translation=${ver.toLowerCase()}`)
          .then(r => { if (!r.ok) throw new Error(`bible-api.com ${r.status}`); return r.json(); })
          .then(data => {
            if (!data?.verses?.length) throw new Error("No verses returned");
            return data.verses.map(v => ({ v: v.verse, t: v.text.replace(/\n/g, " ").trim() }));
          })
      : () => Promise.reject(new Error(`Unsupported translation: ${ver}`));

  const parsed = await loader();
  if (!Array.isArray(parsed) || parsed.length === 0) throw new Error("No verses returned");
  chapterCache[cacheKey] = parsed;
  saveCachedChapter(ver, book, chap, parsed);
  return parsed;
}
